import type { ReactNode } from "react";

export interface BarItem {
  key: string;
  label: ReactNode;
  value: number;
  /** A second line under the label, e.g. what the value is out of. */
  detail?: ReactNode;
}

/**
 * A ranked list with a bar each (dataviz: magnitude, one hue): the label, a thin bar against the
 * largest value and the value as text, so it reads without the colour.
 */
export function BarList({
  items,
  formatValue,
  max,
  empty,
}: {
  items: readonly BarItem[];
  formatValue: (value: number) => string;
  /** The bars' common full length; the largest value by default. */
  max?: number;
  empty?: ReactNode;
}) {
  if (items.length === 0) {
    return empty ? <p className="text-sm text-muted-foreground">{empty}</p> : null;
  }
  const top = max ?? Math.max(...items.map((item) => item.value), 0);
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item.key} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3">
          <div className="min-w-0">
            <p className="truncate text-sm">{item.label}</p>
            {item.detail ? (
              <p className="truncate text-xs text-muted-foreground">{item.detail}</p>
            ) : null}
            <div className="mt-1 h-2 rounded-full bg-muted">
              <div
                className="h-2 rounded-full bg-chart-1"
                style={{ width: `${top > 0 ? Math.max(1, (item.value / top) * 100) : 0}%` }}
              />
            </div>
          </div>
          <span className="text-sm font-medium tabular">{formatValue(item.value)}</span>
        </li>
      ))}
    </ul>
  );
}
